/**
 * HTTP-реализация портов продуктовых функций.
 *
 * Живёт в композиционном корне: сами функции про HTTP не знают
 * (docs/ARCHITECTURE.md §3.3).
 */

import type {
  EntitlementSnapshot,
  InstallationId,
  LanguageCode,
  LayoutId,
  SyncId,
} from '@type2x/shared-contracts';
import type { ApiClient, ApiResult } from '@type2x/adapter-web';
import type {
  AccountsGateway,
  ClaimReply,
  RegisterReply,
  RegistrationForm,
  SessionReply,
  TrialNotice,
  TrialReply,
} from '@type2x/feature-accounts';
import { RegistrationError } from '@type2x/feature-accounts';
import type {
  ProfilePayload,
  RestoredProfile,
  SessionPayload,
  SubmitOutcome,
  SyncGateway,
} from '@type2x/feature-sync';
import type {
  ControlResultPayload,
  ControlSubmission,
  ControlTestGateway,
  RetentionStatus,
} from '@type2x/feature-control-test';
import type {
  AddFriendOutcome,
  FriendsGateway,
  FriendsView,
  LeaderboardBoard,
  LeaderboardGateway,
  LeaderboardKind,
  LeaderboardParticipation,
} from '@type2x/feature-leaderboard';

/** Преобразует ошибку API в понятную пользователю причину. */
function toProblem(result: Extract<ApiResult<unknown>, { ok: false }>): RegistrationError {
  switch (result.error.code) {
    case 'rate_limited':
      return new RegistrationError('rate-limited');
    case 'unauthorized':
      // Сервер отвечает одинаково на неизвестный адрес и неверный пароль:
      // разница в ответах позволила бы перебирать существующие адреса.
      return new RegistrationError('invalid-credentials');
    case 'conflict':
      return new RegistrationError('progress-taken');
    case 'service_unavailable':
      return new RegistrationError('offline');
    case 'validation_error':
      return new RegistrationError('invalid-email');
    default:
      return new RegistrationError('server-error');
  }
}

function unwrap<T>(result: ApiResult<T>): T {
  if (!result.ok) throw toProblem(result);
  return result.data;
}

/** Ответ сервера о сессии в его собственных именах полей. */
interface SessionWire {
  readonly user_id: string;
  readonly email: string;
  readonly email_verified: boolean;
}

/** Приводит ответ о сессии к контракту приложения. */
function toSessionReply(reply: SessionWire): SessionReply {
  return {
    userId: reply.user_id,
    email: reply.email,
    emailVerified: reply.email_verified,
  } as SessionReply;
}

/** Создаёт шлюз аккаунтов поверх HTTP-клиента. */
export function createAccountsGateway(client: ApiClient): AccountsGateway {
  return {
    async register(form: RegistrationForm, installationId: InstallationId) {
      const reply = unwrap(
        await client.post<{
          status: string;
          verification_token: string | null;
          claim_token: string | null;
        }>('/auth/register', {
          email: form.email,
          password: form.password,
          installation_id: installationId,
        }),
      );
      return {
        status: reply.status,
        verificationToken: reply.verification_token,
        claimToken: reply.claim_token,
      } as RegisterReply;
    },

    async exportAccount() {
      return unwrap(await client.post<unknown>('/account/export', {}));
    },

    async logout() {
      unwrap(await client.post<{ status: string }>('/auth/logout'));
    },

    async deleteAccount(password: string) {
      unwrap(await client.delete<{ status: string }>('/account', { password }));
    },

    async requestPasswordReset(email: string) {
      unwrap(await client.post<{ status: string }>('/auth/password-reset/request', { email }));
    },

    async confirmPasswordReset(token: string, password: string) {
      unwrap(
        await client.post<{ status: string }>('/auth/password-reset/confirm', { token, password }),
      );
    },

    async login(form: RegistrationForm) {
      const reply = unwrap(
        await client.post<SessionWire>('/auth/login', {
          email: form.email,
          password: form.password,
        }),
      );
      return toSessionReply(reply);
    },

    async resendVerification(email: string) {
      unwrap(await client.post<{ status: string }>('/auth/verify-email/resend', { email }));
    },

    async currentSession() {
      const result = await client.get<SessionWire>('/auth/me');
      // 401 — обычный ответ для гостя, а не сбой: сессии просто нет, и
      // спрашивать «кто вошёл» имеет право кто угодно.
      if (!result.ok) return null;

      return toSessionReply(result.data);
    },

    async registrationStatus(claimToken: string) {
      const result = await client.post<{
        verified: boolean;
        user_id: string | null;
        email: string | null;
        email_verified: boolean;
      }>('/auth/registration-status', { claim_token: claimToken });

      if (!result.ok || !result.data.verified) return null;
      if (result.data.user_id === null || result.data.email === null) return null;

      return {
        userId: result.data.user_id,
        email: result.data.email,
        emailVerified: result.data.email_verified,
      } as SessionReply;
    },

    async verifyEmail(token: string) {
      const reply = unwrap(
        await client.post<SessionWire>('/auth/verify-email', {
          token,
        }),
      );
      return toSessionReply(reply);
    },

    async claim(installationId: InstallationId, syncId: SyncId) {
      const reply = unwrap(
        await client.post<{ claimed: boolean; already_claimed: boolean }>('/auth/claim', {
          installation_id: installationId,
          sync_id: syncId,
        }),
      );
      return { claimed: reply.claimed, alreadyClaimed: reply.already_claimed } as ClaimReply;
    },

    async trialStatus() {
      const reply = unwrap(
        await client.get<{ active: boolean; days_left: number; notice?: TrialNotice }>(
          '/trial/status',
        ),
      );
      // Уведомление считает сервер (ТЗ FR-SUB-03). Его отсутствие в ответе —
      // не молчание об окончании, а старый сервер: тогда говорить не о чем.
      return {
        active: reply.active,
        daysLeft: reply.days_left,
        notice: reply.notice ?? 'none',
      } as TrialReply;
    },
  };
}

/** Ответ сервера на приём итога сессии. */
interface SessionSubmitReply {
  readonly duplicate: boolean;
  readonly awarded_xp: number;
}

/** Ответ сервера с прогрессом по языку. */
interface ProgressReply {
  readonly profiles: readonly {
    readonly language: LanguageCode;
    readonly layout_id: LayoutId;
    readonly baseline: {
      readonly wpm: number;
      readonly cpm: number;
      readonly accuracy: number;
    } | null;
    readonly best_verified_wpm: number;
    readonly units: readonly {
      readonly unit_id: string;
      readonly layout_id: LayoutId;
      readonly attempts: number;
      readonly first_try_correct: number;
      readonly error_rate: number;
      readonly correction_rate: number;
      readonly median_latency_ms: number;
      readonly p95_latency_ms: number;
      readonly mastery_score: number;
      readonly consecutive_successes: number;
      readonly last_seen_at: string | null;
      readonly next_review_at: string;
    }[];
  }[];
}

/** Переводит прогресс языка из ответа сервера в вид приложения. */
function toRestoredProfile(profile: ProgressReply['profiles'][number]): RestoredProfile {
  return {
    language: profile.language,
    layoutId: profile.layout_id,
    baseline: profile.baseline,
    bestVerifiedWpm: profile.best_verified_wpm,
    units: profile.units.map((unit) => ({
      unitId: unit.unit_id,
      layoutId: unit.layout_id,
      attempts: unit.attempts,
      firstTryCorrect: unit.first_try_correct,
      errorRate: unit.error_rate,
      correctionRate: unit.correction_rate,
      medianLatencyMs: unit.median_latency_ms,
      p95LatencyMs: unit.p95_latency_ms,
      masteryScore: unit.mastery_score,
      consecutiveSuccesses: unit.consecutive_successes,
      // Метки последнего показа у строк, записанных до этой возможности, нет:
      // тогда за неё отвечает срок повторения — он от неё и считался.
      lastSeenAt: unit.last_seen_at ?? unit.next_review_at,
      nextReviewAt: unit.next_review_at,
    })),
  };
}

/** Ответ сервера со снимком доступов. */
interface EntitlementsReply {
  readonly state: EntitlementSnapshot['state'];
  readonly entitlements: EntitlementSnapshot['entitlements'];
  readonly current_period_ends_at: string | null;
  readonly issued_at: string;
}

/**
 * Переводит ошибку API в исход отправки.
 *
 * Разделение важно: отсутствие аккаунта и недоступность сервера требуют
 * повтора, а отвергнутое содержимое — нет.
 */
function toSubmitOutcome(error: { readonly code: string }): SubmitOutcome {
  switch (error.code) {
    case 'unauthorized':
    case 'forbidden':
      return { kind: 'unauthenticated' };
    case 'service_unavailable':
    case 'rate_limited':
    case 'internal_error':
      return { kind: 'unavailable' };
    default:
      return { kind: 'rejected', reason: error.code };
  }
}

/** Преобразует итог сессии в форму, ожидаемую сервером. */
function toWirePayload(payload: SessionPayload): Record<string, unknown> {
  return {
    sync_id: payload.syncId,
    session_id: payload.sessionId,
    mode: payload.mode,
    language: payload.language,
    layout_id: payload.layoutId,
    started_at: payload.startedAt,
    duration_ms: payload.durationMs,
    metrics: payload.metrics,
    unit_outcomes: payload.unitOutcomes.map((outcome) => ({
      unit_id: outcome.unitId,
      layout_id: outcome.layoutId,
      attempts: outcome.attempts,
      first_try_correct: outcome.firstTryCorrect,
      error_rate: outcome.errorRate,
      correction_rate: outcome.correctionRate,
      median_latency_ms: outcome.medianLatencyMs,
      p95_latency_ms: outcome.p95LatencyMs,
      mastery_score: outcome.masteryScore,
      consecutive_successes: outcome.consecutiveSuccesses,
      last_seen_at: outcome.lastSeenAt,
      next_review_at: outcome.nextReviewAt,
    })),
    error_breakdown: payload.errorBreakdown,
    is_controlled: payload.isControlled,
  };
}

/** Создаёт шлюз синхронизации поверх HTTP-клиента. */
export function createSyncGateway(client: ApiClient): SyncGateway {
  return {
    async submitSession(payload: SessionPayload): Promise<SubmitOutcome> {
      const result = await client.post<SessionSubmitReply>('/sessions', toWirePayload(payload));
      if (!result.ok) return toSubmitOutcome(result.error);

      return {
        kind: 'accepted',
        duplicate: result.data.duplicate,
        awardedXp: result.data.awarded_xp,
      };
    },

    async upsertProfile(payload: ProfilePayload): Promise<SubmitOutcome> {
      // Профиль языка на сервере не заводился никогда: `PUT` был написан,
      // CORS для него починен на этапе X — и не вызван ни разу. Без записи
      // сервер отвечал `rating: null` на каждый контрольный тест, не
      // засчитывал сохранение навыка и отказывал в участии в рейтинге.
      const result = await client.put<unknown>('/workspace/profiles', {
        language: payload.language,
        layout_id: payload.layoutId,
        baseline:
          payload.baseline === null
            ? null
            : {
                wpm: payload.baseline.wpm,
                cpm: payload.baseline.cpm,
                accuracy: payload.baseline.accuracy,
              },
      });
      if (!result.ok) return toSubmitOutcome(result.error);

      // Ответ — сам профиль; отправителю от него ничего не нужно.
      return { kind: 'accepted', duplicate: false, awardedXp: 0 };
    },

    async fetchProgress(): Promise<readonly RestoredProfile[] | null> {
      const result = await client.get<ProgressReply>('/workspace/progress');
      // Гость и обрыв связи — одно и то же «вернуть нечего»: восстанавливать
      // не из чего, и различать их здесь незачем. Пустой список — другое дело:
      // это ответ сервера «в аккаунте пусто».
      if (!result.ok) return null;

      return result.data.profiles.map(toRestoredProfile);
    },

    async fetchEntitlements(): Promise<EntitlementSnapshot | null> {
      const result = await client.get<EntitlementsReply>('/entitlements');
      if (!result.ok) return null;

      return {
        state: result.data.state,
        entitlements: result.data.entitlements,
        currentPeriodEndsAt: result.data.current_period_ends_at,
        issuedAt: result.data.issued_at,
      };
    },
  };
}

/** Ответ сервера на контрольный результат. */
interface VerifiedTestReply {
  readonly accepted: boolean;
  readonly reasons: string[];
  readonly rating: number | null;
  readonly retention_counted: boolean;
  readonly retention_score: number;
}

/** Ответ сервера о сроке отложенной проверки. */
interface RetentionReply {
  readonly due: boolean;
  readonly due_at: string | null;
  readonly score: number;
}

/**
 * Превращает ошибку API в названную причину отказа.
 *
 * Раньше любой неуспех сводился к `null`, и экран показывал «нет связи с
 * сервером» — в том числе тогда, когда связь была, а сервер отвечал «требуется
 * вход». Гость проходил контрольный тест и получал неправду о происходящем.
 */
function toControlSubmission(error: { readonly code: string }): ControlSubmission {
  switch (error.code) {
    case 'unauthorized':
    case 'forbidden':
      return { kind: 'needs-account' };
    case 'service_unavailable':
      return { kind: 'offline' };
    default:
      // Лимит запросов, отказ проверки, сбой: сервер ответил, результат не
      // принят. Что именно — знает сервер, и это не «нет связи».
      return { kind: 'not-accepted' };
  }
}

/** Создаёт шлюз контрольного теста поверх HTTP-клиента. */
export function createControlTestGateway(client: ApiClient): ControlTestGateway {
  return {
    async submit(payload: ControlResultPayload): Promise<ControlSubmission> {
      const result = await client.post<VerifiedTestReply>('/verified-tests', {
        sync_id: payload.syncId,
        language: payload.language,
        layout_id: payload.layoutId,
        wpm: payload.wpm,
        accuracy: payload.accuracy,
        duration_ms: Math.round(payload.durationMs),
        characters: payload.characters,
        unseen_text: payload.unseenText,
        signals: {
          paste_detected: payload.signals.pasteDetected,
          synthetic_events: payload.signals.syntheticEvents,
          focus_loss_count: payload.signals.focusLossCount,
          interval_variation: payload.signals.intervalVariation,
          min_interval_ms: payload.signals.minIntervalMs,
          identical_interval_ratio: payload.signals.identicalIntervalRatio,
          window_focused: payload.signals.windowFocused,
        },
      });
      if (!result.ok) return toControlSubmission(result.error);

      return {
        kind: 'verdict',
        verdict: {
          accepted: result.data.accepted,
          reasons: result.data.reasons,
          rating: result.data.rating,
          retentionCounted: result.data.retention_counted,
          retentionScore: result.data.retention_score,
        },
      };
    },

    async retentionStatus(language): Promise<RetentionStatus | null> {
      const result = await client.get<RetentionReply>(
        `/verified-tests/retention?language=${encodeURIComponent(language)}`,
      );
      if (!result.ok) return null;

      return { due: result.data.due, dueAt: result.data.due_at, score: result.data.score };
    },
  };
}

/** Ответ сервера с таблицей лидеров. */
interface LeaderboardReply {
  readonly kind: LeaderboardKind;
  readonly language: string;
  readonly version: string;
  readonly participating: boolean;
  readonly computed_at: string;
  readonly entries: readonly {
    readonly rank: number;
    readonly display_name: string;
    readonly rating: number;
    readonly verified_wpm: number;
    readonly accuracy: number;
    /**
     * Величина порядка этой таблицы. Необязательна: во время выкладки клиент
     * может первым делом попасть на сервер, который её ещё не шлёт, и лучше
     * показать рейтинг, чем пустую клетку.
     */
    readonly metric?: number;
    readonly is_you: boolean;
  }[];
}

/**
 * Создаёт шлюз таблиц лидеров поверх HTTP-клиента.
 *
 * Гостю сервер отвечает «требуется вход», и это не ошибка приложения:
 * рейтинг считается по засчитанным контрольным результатам, а они привязаны к
 * аккаунту. Такой ответ превращается в `null` — «сказать нечего».
 */
export function createLeaderboardGateway(client: ApiClient): LeaderboardGateway {
  return {
    async board(kind: LeaderboardKind, language: string): Promise<LeaderboardBoard | null> {
      const result = await client.get<LeaderboardReply>(
        `/leaderboards/${kind}?language=${encodeURIComponent(language)}`,
      );
      if (!result.ok) return null;

      return {
        kind: result.data.kind,
        language: result.data.language,
        version: result.data.version,
        participating: result.data.participating,
        computedAt: result.data.computed_at,
        rows: result.data.entries.map((entry) => ({
          rank: entry.rank,
          displayName: entry.display_name,
          rating: entry.rating,
          verifiedWpm: entry.verified_wpm,
          accuracy: entry.accuracy,
          metric: entry.metric ?? entry.rating,
          isYou: entry.is_you,
        })),
      };
    },

    async updateParticipation(participation: LeaderboardParticipation): Promise<void> {
      // Согласие — явное действие человека, и оно обязано доехать до сервера:
      // хранить его только на устройстве значит обещать участие, которого не
      // будет (ТЗ FR-LB-06).
      unwrap(
        await client.patch<unknown>('/workspace/privacy', {
          language: participation.language,
          ...(participation.optIn === undefined ? {} : { leaderboard_opt_in: participation.optIn }),
          ...(participation.displayName === undefined
            ? {}
            : { display_name: participation.displayName }),
        }),
      );
    },
  };
}

/** Ответ сервера со списком друзей. */
interface FriendsReply {
  readonly code: string;
  readonly max_friends: number;
  readonly friends: readonly {
    readonly link_id: string;
    readonly display_name: string;
    readonly added_at: string;
    readonly participating: boolean;
  }[];
}

/** Превращает ответ сервера в список друзей. */
function toFriendsView(reply: FriendsReply): FriendsView {
  return {
    code: reply.code,
    maxFriends: reply.max_friends,
    friends: reply.friends.map((friend) => ({
      linkId: friend.link_id,
      displayName: friend.display_name,
      addedAt: friend.added_at,
      participating: friend.participating,
    })),
  };
}

/**
 * Создаёт шлюз дружеского рейтинга поверх HTTP-клиента.
 *
 * Причина отказа не прячется: «кода нет» и «список переполнен» — разные
 * вещи, и человек, вводивший код, должен знать, какая из них случилась.
 */
export function createFriendsGateway(client: ApiClient): FriendsGateway {
  return {
    async view(): Promise<FriendsView | null> {
      const result = await client.get<FriendsReply>('/friends');
      return result.ok ? toFriendsView(result.data) : null;
    },

    async add(code: string): Promise<AddFriendOutcome> {
      const result = await client.post<FriendsReply>('/friends', { code });
      if (result.ok) return 'added';

      switch (result.error.code) {
        case 'not_found':
          return 'not-found';
        case 'conflict':
          return 'full';
        default:
          return 'failed';
      }
    },

    async remove(linkId: string): Promise<boolean> {
      const result = await client.delete<unknown>(`/friends/${encodeURIComponent(linkId)}`, {});
      return result.ok;
    },

    async board(language: string): Promise<LeaderboardBoard | null> {
      const result = await client.get<LeaderboardReply>(
        `/friends/leaderboard?language=${encodeURIComponent(language)}`,
      );
      if (!result.ok) return null;

      return {
        kind: result.data.kind,
        language: result.data.language,
        version: result.data.version,
        participating: result.data.participating,
        computedAt: result.data.computed_at,
        rows: result.data.entries.map((entry) => ({
          rank: entry.rank,
          displayName: entry.display_name,
          rating: entry.rating,
          verifiedWpm: entry.verified_wpm,
          accuracy: entry.accuracy,
          metric: entry.metric ?? entry.rating,
          isYou: entry.is_you,
        })),
      };
    },
  };
}
